import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { authService } from '../services/auth.service'
import {
  User,
  AuthResponse,
  LoginCredentials,
  LoginResponse,
  RegisterFamilyData,
  VerifyTwoFactorParams,
} from '../types'

export function useAuth() {
  const [isInitialized, setIsInitialized] = useState(false)
  const queryClient = useQueryClient()

  // Get current user
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: () => authService.getProfile(),
    enabled: authService.isAuthenticated() && isInitialized,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  // Initialize auth state
  useEffect(() => {
    const token = authService.getToken()
    if (token) {
      setIsInitialized(true)
    } else {
      setIsInitialized(true)
    }
  }, [])

  // Persist a completed login/register response (token + user)
  const persistAuth = (data: AuthResponse) => {
    // Normalize user data to ensure phone field exists
    const normalizedUser = {
      ...data.user,
      phone: data.user.phone || data.user.phoneNumber || undefined,
    }
    authService.setAuthData(data.access_token, normalizedUser)
    queryClient.setQueryData(['auth', 'user'], normalizedUser)
    queryClient.invalidateQueries({ queryKey: ['auth'] })
  }

  // Login mutation — for 2FA-enabled accounts the response is a challenge
  // token and NOTHING is stored; the caller completes login via verify2FA.
  const loginMutation = useMutation({
    mutationFn: (credentials: LoginCredentials) => authService.login(credentials),
    onSuccess: (data: LoginResponse) => {
      if ((data as { twoFactorRequired?: boolean }).twoFactorRequired) {
        return
      }
      persistAuth(data as AuthResponse)
    },
  })

  // Two-factor verification (second login step)
  const verifyTwoFactorMutation = useMutation({
    mutationFn: (params: VerifyTwoFactorParams) => authService.verifyTwoFactor(params),
    onSuccess: (data) => persistAuth(data),
  })

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (userData: RegisterFamilyData) => authService.registerFamily(userData),
    onSuccess: (data) => persistAuth(data),
  })

  // Logout function
  const logout = () => {
    authService.clearAuthData()
    queryClient.setQueryData(['auth', 'user'], null)
    queryClient.clear()
  }

  // Update user data function (used after profile updates)
  const updateUser = (updatedUser: User) => {
    // Ensure backward compatibility by mapping phoneNumber to phone if phone is not present
    const normalizedUser = {
      ...updatedUser,
      phone: updatedUser.phone || updatedUser.phoneNumber || undefined,
    }
    authService.updateStoredUser(normalizedUser)
    queryClient.setQueryData(['auth', 'user'], normalizedUser)
    queryClient.invalidateQueries({ queryKey: ['auth'] })
  }

  // Get user from localStorage if query hasn't returned data yet
  const storedUser = authService.getStoredUser()
  const currentUser = user || storedUser

  return {
    user: currentUser,
    isLoading: isLoading || !isInitialized,
    isAuthenticated: !!currentUser,
    error,
    login: loginMutation.mutateAsync,
    register: registerMutation.mutateAsync,
    verifyTwoFactor: verifyTwoFactorMutation.mutateAsync,
    logout,
    updateUser,
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isVerifyingTwoFactor: verifyTwoFactorMutation.isPending,
  }
}